import React, { useState } from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../components/common/PageHeader';
import { EnvelopeIcon, PhoneIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { Loader2 } from 'lucide-react';

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const About = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const form = e.target;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://formsubmit.co/ajax/detaydeck@gmail.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(Object.fromEntries(formData))
      });

      const data = await response.json();

      if (data.success) {
        // Form başarıyla gönderildi, yönlendirme yap
        window.location.href = window.location.hostname === 'localhost'
          ? 'http://localhost:3000/tesekkurler'
          : 'https://detaydeck.com/tesekkurler';
      } else {
        setStatus({ type: 'error', message: 'Bir hata oluştu. Lütfen tekrar deneyin.' });
        setIsSubmitting(false);
      }
    } catch (error) {
      setStatus({ type: 'error', message: 'Bir hata oluştu. Lütfen tekrar deneyin.' });
      setIsSubmitting(false);
    }
  };

  const stats = [
    { number: '15+', label: 'Yıllık Deneyim' },
    { number: '500+', label: 'Tamamlanan Proje' },
    { number: '100+', label: 'Mutlu Müşteri' },
    { number: '50+', label: 'Uzman Ekip' }
  ];

  const values = [
    {
      title: 'Kalite',
      description: 'En yüksek kalite standartlarında üretim ve hizmet sunuyoruz.',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
        </svg>
      )
    },
    {
      title: 'İnovasyon',
      description: 'Sürekli gelişim ve yenilikçi çözümler üretiyoruz.',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      )
    },
    {
      title: 'Güvenilirlik',
      description: 'Müşterilerimize karşı şeffaf ve güvenilir bir yaklaşım sergiliyoruz.',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Detay Deck Hakkında"
        description="2016 yılından bu yana kompozit deck kalitesini tüm dünyaya tanıtmak ve sektörde önemli bir rol almak amacıyla hizmet veriyoruz."
        backgroundImage="/images/hero-bg.jpg"
      />

      {/* About Content Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="initial"
              animate="animate"
              variants={fadeIn}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Biz Kimiz?
              </h2>
              <p className="text-gray-600 mb-6">
                Detay Deck, 2016 yılında küçük, orta ve büyük ölçekli projelerin geliştirilmesi, tüm dünyaya
                kompozit deck kalitesini tanıtmak ve sektörde önemli bir rol almak amacıyla İstanbul'da
                kurulmuştur. Kuruluşundan beri süre gelen profesyonellik, yaratıcılık ve dürüstlük
                özellikleriyle istikrarlı bir büyüme göstererek sektörde saygın bir yer edinmiştir.
              </p>
              <p className="text-gray-600">
                Firmamız güçlü idari ve mali birikimi ile sektörde öncü firmalardan biri olmayı sürdürmektedir.
                Büyük projeleri hayata geçirmeye devam eden firmamız halihazırda Türkiye'nin her bölgesinde
                kaliteli, nitelikli birçok projeye imza atmıştır.
              </p>
            </motion.div>
            <motion.div
              initial="initial"
              animate="animate"
              variants={fadeIn}
              className="relative"
            >
              <img
                src="/images/veranda-bg.jpg"
                alt="About Us"
                className="rounded-lg shadow-xl"
              />
              <div className="absolute inset-0 bg-[#dbc582]/10 rounded-lg" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial="initial"
              animate="animate"
              variants={fadeIn}
              className="bg-gray-50 p-8 rounded-lg shadow-lg"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Vizyonumuz</h3>
              <p className="text-gray-600 mb-4">
                DetayDeck, geleceğin inovasyonunu ve estetiğini yaşam alanlarına taşıyan öncü bir çözüm
                sağlayıcısıdır. Biz, yaşam alanlarınızı dönüştürerek, her detayda mükemmelliği hedefler ve estetik bir
                dokunuşla nitelikli mekanlar oluştururuz.
              </p>
              <p className="text-gray-600">
                Estetik, fonksiyonellik ve yenilikçilikle öne çıkarak, müşterilerimize kaliteli ve unutulmaz
                mekanlar sunuyoruz. Sürekli gelişen teknoloji ve tasarım trendlerini takip ederek, müşterilerimize
                ileri düzeyde inovatif çözümler sunarız.
              </p>
            </motion.div>
            <motion.div
              initial="initial"
              animate="animate"
              variants={fadeIn}
              className="bg-gray-50 p-8 rounded-lg shadow-lg"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Misyonumuz</h3>
              <p className="text-gray-600 mb-4">
                Detay Deck olarak misyonumuz, yaşam alanlarınızı daha işlevsel, estetik ve
                sürdürülebilir hale getirerek müşteri memnuniyetini en üst düzeye çıkarmaktır.
                Her detayda mükemmeli arayarak, inovasyon ve çevresel sorumluluk bilinciyle
                hareket ederiz.
              </p>
              <p className="text-gray-600">
                DetayDeck, tasarım ve teknoloji alanındaki en son gelişmeleri takip eder.
                İnovasyon ve estetik, projelerimizin temel taşlarıdır. Sıradışı çözümler ve özgün
                tasarımlarla müşterilerimizin hayalini gerçeğe dönüştürürüz.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial="initial"
                animate="animate"
                variants={fadeIn}
                className="text-center"
              >
                <p className="text-4xl font-bold text-[#dbc582]">{stat.number}</p>
                <p className="mt-2 text-gray-600">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Değerlerimiz</h2>
            <p className="mt-4 text-xl text-gray-600">
              Müşterilerimize en iyi hizmeti sunmak için benimsediğimiz temel değerler
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial="initial"
                animate="animate"
                variants={fadeIn}
                className="bg-white rounded-lg shadow-lg p-8"
              >
                <div className="text-[#dbc582] mb-4">{value.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {value.title}
                </h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="p-12 bg-[#dbc582]">
                <h3 className="text-2xl font-bold text-white mb-6">
                  Bizimle İletişime Geçin
                </h3>
                <p className="text-white/90 mb-8">
                  Projeleriniz için detaylı bilgi almak veya randevu oluşturmak için bize ulaşın. Size en kısa sürede dönüş yapacağız.
                </p>
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <PhoneIcon className="h-6 w-6 text-white" />
                    <div className="flex flex-col">
                      <a href="tel:+905387416525" className="text-white hover:text-white/80 transition-colors">
                        +90 538 741 65 25
                      </a>
                      <a href="tel:+905321682948" className="text-white hover:text-white/80 transition-colors">
                        +90 532 168 29 48
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <EnvelopeIcon className="h-6 w-6 text-white" />
                    <a href="mailto:detaydeck@gmail.com" className="text-white hover:text-white/80 transition-colors">
                      detaydeck@gmail.com
                    </a>
                  </div>
                  <div className="flex items-center space-x-4">
                    <MapPinIcon className="h-6 w-6 text-white" />
                    <div className="text-white">
                      <div>Ahmediye, Çatalca Yolu Cd. No:201, 34537</div>
                      <div>Büyükçekmece/İstanbul</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-12 relative">
                {status && status.message && (
                  <div className={`p-4 rounded-lg mb-4 ${status.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {status.message}
                  </div>
                )}
                <form onSubmit={handleSubmit} className="space-y-6">
                  <input type="hidden" name="_subject" value="Yeni İletişim Formu Mesajı!" />
                  <input type="hidden" name="_template" value="table" />
                  <input type="hidden" name="_captcha" value="false" />

                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Adınız Soyadınız
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dbc582] focus:ring-[#dbc582]"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      E-posta Adresiniz
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dbc582] focus:ring-[#dbc582]"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                      Telefon Numaranız
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dbc582] focus:ring-[#dbc582]"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                      Mesajınız
                    </label>
                    <textarea
                      name="message"
                      rows={4}
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dbc582] focus:ring-[#dbc582]"
                    />
                  </div>
                  <div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#dbc582] hover:bg-[#c4b070] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#dbc582] transition-colors ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                        }`}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Gönderiliyor...
                        </span>
                      ) : (
                        'Gönder'
                      )}
                    </button>
                  </div>
                </form>

                {/* Loading Overlay */}
                {isSubmitting && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
                    <div className="bg-white p-8 rounded-xl shadow-lg text-center">
                      <Loader2 className="w-12 h-12 animate-spin text-[#dbc582] mx-auto mb-4" />
                      <p className="text-gray-700">Mesajınız Gönderiliyor...</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
